import React, { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase';
import { ChevronDownIcon, ChevronRightIcon, EnvelopeIcon, ArrowUpIcon, ArrowDownIcon, LockClosedIcon } from '@heroicons/react/20/solid';

const EmailHistoryPanel = ({ recipientEmail, maxItems = 20, className = '' }) => {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedEmailId, setExpandedEmailId] = useState(null);
  const [emailBody, setEmailBody] = useState({});
  const [loadingBody, setLoadingBody] = useState({});

  // PIN state
  const [pin, setPin] = useState('');
  const [pinVerified, setPinVerified] = useState(false);
  const [pinError, setPinError] = useState('');

  const fetchEmailHistory = async (accessPin) => {
    if (!recipientEmail) return;
    console.log('[EmailHistory] Fetching Gmail history for:', recipientEmail);
    setLoading(true);
    setError(null);
    try {
      const getEmailHistory = httpsCallable(functions, 'getEmailHistory');
      const result = await getEmailHistory({
        contactEmail: recipientEmail,
        maxResults: maxItems,
        pin: accessPin
      });
      console.log('[EmailHistory] Got', result.data.emails.length, 'emails from Gmail');
      setEmails(result.data.emails);
      setPinVerified(true);
    } catch (err) {
      console.error('[EmailHistory] Error:', err);
      if (err.code === 'functions/permission-denied') {
        setPinError('Invalid access code');
        setPinVerified(false);
      } else {
        setError(err.message || 'Could not load email history.');
        setPinVerified(true); // PIN was correct, just another error
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePinSubmit = (e) => {
    e.preventDefault();
    setPinError('');
    fetchEmailHistory(pin);
  };

  const loadFullBody = async (messageId) => {
    if (emailBody[messageId]) return;
    setLoadingBody(prev => ({ ...prev, [messageId]: true }));
    try {
      const getEmailDetail = httpsCallable(functions, 'getEmailDetail');
      const result = await getEmailDetail({ messageId, pin });
      setEmailBody(prev => ({ ...prev, [messageId]: result.data.body }));
    } catch (err) {
      console.error('[EmailHistory] Error loading body:', err);
    } finally {
      setLoadingBody(prev => ({ ...prev, [messageId]: false }));
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
    } catch {
      return dateStr;
    }
  };

  const parseEmailAddress = (addr) => {
    if (!addr) return '';
    const match = addr.match(/^(.+?)\s*<(.+)>$/);
    if (match) return match[1].trim();
    return addr;
  };

  // PIN entry screen
  if (!pinVerified) {
    return (
      <div className={`py-6 ${className}`}>
        <form onSubmit={handlePinSubmit} className="flex flex-col items-center space-y-3">
          <LockClosedIcon className="h-8 w-8 text-gray-400" />
          <p className="text-sm text-gray-600">Enter access code to view email history</p>
          <div className="flex items-center space-x-2">
            <input
              type="password"
              value={pin}
              onChange={(e) => { setPin(e.target.value); setPinError(''); }}
              placeholder="Access code"
              className="w-32 px-3 py-2 border border-gray-300 rounded-md text-center text-sm focus:ring-blue-500 focus:border-blue-500"
              autoFocus
            />
            <button
              type="submit"
              disabled={loading || !pin}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:bg-blue-300"
            >
              {loading ? 'Verifying...' : 'Unlock'}
            </button>
          </div>
          {pinError && <p className="text-xs text-red-500">{pinError}</p>}
        </form>
      </div>
    );
  }

  if (loading) {
    return (
      <div className={`flex items-center justify-center py-8 ${className}`}>
        <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-blue-500"></div>
        <span className="ml-2 text-sm text-gray-500">Loading emails from Gmail...</span>
      </div>
    );
  }

  if (error) {
    return <p className={`text-sm text-red-500 italic py-4 ${className}`}>{error}</p>;
  }

  if (emails.length === 0) {
    return (
      <div className={`flex items-center justify-center py-8 text-gray-400 ${className}`}>
        <EnvelopeIcon className="h-5 w-5 mr-2" />
        <p className="text-sm italic">No email history found with this address.</p>
      </div>
    );
  }

  return (
    <div className={`divide-y divide-gray-100 ${className}`}>
      {emails.map((email) => {
        const isExpanded = expandedEmailId === email.id;

        return (
          <div key={email.id}>
            <div
              className="flex items-center py-3 px-2 cursor-pointer hover:bg-gray-50 rounded"
              onClick={() => setExpandedEmailId(isExpanded ? null : email.id)}
            >
              {isExpanded ? (
                <ChevronDownIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
              ) : (
                <ChevronRightIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
              )}

              {email.isInbound ? (
                <span className="ml-2 px-1.5 py-0.5 rounded text-xs font-medium flex-shrink-0 bg-blue-100 text-blue-700 flex items-center">
                  <ArrowDownIcon className="h-3 w-3 mr-0.5" />
                  Received
                </span>
              ) : (
                <span className="ml-2 px-1.5 py-0.5 rounded text-xs font-medium flex-shrink-0 bg-green-100 text-green-700 flex items-center">
                  <ArrowUpIcon className="h-3 w-3 mr-0.5" />
                  Sent
                </span>
              )}

              <span className="ml-2 text-sm text-gray-800 truncate flex-1">
                {email.subject || '(no subject)'}
              </span>

              <span className="ml-2 text-xs text-gray-400 flex-shrink-0 whitespace-nowrap">
                {formatDate(email.date)}
              </span>
            </div>

            {isExpanded && (
              <div className="ml-7 mb-3 pl-3 border-l-2 border-gray-200 space-y-1.5 text-sm">
                <p className="text-gray-600">
                  <span className="font-medium text-gray-700">From:</span> {parseEmailAddress(email.from)}
                </p>
                <p className="text-gray-600">
                  <span className="font-medium text-gray-700">To:</span> {parseEmailAddress(email.to)}
                </p>
                <p className="text-gray-600">
                  <span className="font-medium text-gray-700">Date:</span> {formatDateTime(email.date)}
                </p>
                <p className="text-gray-600">
                  <span className="font-medium text-gray-700">Subject:</span> {email.subject}
                </p>

                {email.snippet && (
                  <div className="mt-2 p-2 bg-gray-50 rounded text-xs text-gray-600">
                    {email.snippet}
                  </div>
                )}

                {!emailBody[email.id] ? (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      loadFullBody(email.id);
                    }}
                    disabled={loadingBody[email.id]}
                    className="mt-2 text-xs text-blue-600 hover:text-blue-700 underline"
                  >
                    {loadingBody[email.id] ? 'Loading...' : 'View full email'}
                  </button>
                ) : (
                  <div
                    className="mt-2 p-3 bg-white rounded border border-gray-200 text-xs text-gray-700 max-h-80 overflow-y-auto"
                    dangerouslySetInnerHTML={{ __html: emailBody[email.id] }}
                  />
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default EmailHistoryPanel;
